// React
import * as React from "react";
import { GaugeHelperHighlight, getValueFromHighlights, validateHighlightsWithMsg } from "./GaugesHelper";

import _ from 'lodash';

// Material UI components
import { Table, TableBody, TableHeader, TableHeaderColumn, TableRow, TableRowColumn } from 'material-ui/Table';
import TextField from 'material-ui/TextField';
import FlatButton from 'material-ui/FlatButton';
import AddIcon from 'material-ui/svg-icons/content/add';
import IconButton from 'material-ui/IconButton';
import DeleteIcon from 'material-ui/svg-icons/action/delete';

// Internal components
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { SettingsList } from '@Primitive/SettingsList';
import { SettingsDialogConf, SettingsType } from '@Primitive/SettingsListType';
import { IWidgetThingsClass } from "../Utils/IWidgetThingsClass";
import { isDefined, IsNullOrEmpty, isNumeric } from '@Utils/Micro';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing } from '@UI/Widgets/WidgetStructs';
import { WebNode } from '@API/WebNode';
import { WebThing, WebThingPort } from '@API/WebThing';
import { Plegma } from '@API/Api';
import { ReduxThingState, StateStatus, WebThingPortType } from '@API/WebNodeTypes';
import type { LocalizationMap } from '@Primitive/Localization/Localization';
import { DebugEx } from '@Utils/DebugEx';
import { Knob as KnobPrimitive } from "@Primitive/Knob";
import { StateManager } from "@Utils/StateManager";
// =======================================================================================================

export interface KnobData {
    KnobSettings: KnobSettings;
}

export interface KnobSettings {
    MinValue: number;
    MaxValue: number;
    Step: number;
    BackgroundColor: string;
    ForegroundColor: string;
    DisplayInput: boolean;
    EnableInput: boolean;
    EnableOutput: boolean;
    OutputMessages: IOutputMessages[];
    highlights: GaugeHelperHighlight[];
    StreamingOutput: boolean;
    reverseMode: boolean;
}

interface IOutputMessages {
    Value: number;
    Message: string;
    OutOfBounds: boolean;
}

export interface KnobState {
    value: number;
}

// ------------------------------------------------------------------------------------------------------
class Knob extends React.Component<WidgetsThingsStructs.PartialIWidgetThingsProps<KnobData>, KnobState>{
    // ---------------------------------------------------------------------------------------------------
    private key: string;
    // ---------------------------------------------------------------------------------------------------
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<KnobData>) {
        super(props);
        this.state = { value: this.props.WidgetData.KnobSettings.MinValue };
    }
    // ------------------------------------------------------
    stateUpdate = (state: ReduxThingState, forceUpdate: boolean, changedSubKeys: string[]) => {
        for (const portKey of changedSubKeys) {
            let port = state.PortStates[portKey];
            if (port != null &&
                port.LastStateStatus == StateStatus.Valid)
                this.updatePortValue(portKey, port.State);
        }
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidMount() {
        if (_.has(this.props.PortsStates, PortKeys.Input) &&
            this.props.PortsStates[PortKeys.Input].LastStateStatus == StateStatus.Valid)
            this.updatePortValue(PortKeys.Input, this.props.PortsStates[PortKeys.Input].State);

        // Link update port
        let stateManager = StateManager.GetInstanceKey(this.props.NodeKey);
        if (isDefined(stateManager))
            stateManager.RegisterCB(this.props.ThingKey, this.stateUpdate);
    }
    // ---------------------------------------------------------------------------------------------------
    componentWillUnmount() {
        let stateManager = StateManager.GetInstanceKey(this.props.NodeKey);
        if (isDefined(stateManager))
            stateManager.UnRegisterCB(this.props.ThingKey, this.stateUpdate);
    }
    // ---------------------------------------------------------------------------------------------------
    render() {

        const settings = this.props.WidgetData.KnobSettings;
        const value = this.state.value;

        // Get knob's foreground color.
        let fgColor = settings.ForegroundColor;
        var res = getValueFromHighlights(value, settings.highlights);
        if (res.success === true)
            fgColor = res.color;

        // Find min/max values.
        let minValue = settings.MinValue;
        let maxValue = settings.MaxValue;
        if (this.state.value < settings.MinValue)
            minValue = this.state.value;
        else if (this.state.value > settings.MaxValue)
            maxValue = this.state.value;

        const space = 5;
        const minSizeValue = this.props.width > this.props.height
            ? this.props.height
            : this.props.width;

        return (
            <div
                key={this.key}
                style={{
                    width: minSizeValue,
                    height: minSizeValue,
                    margin: '0 auto',
                    padding: space
                }}>
                <KnobPrimitive
                    value={value}
                    onChange={this.handleChange}
                    onChangeEnd={this.handleChangeEnd}
                    width={minSizeValue - 2 * space}
                    height={minSizeValue - 2 * space}
                    min={minValue}
                    max={maxValue}
                    clockwise={!settings.reverseMode}
                    fgColor={fgColor}
                    bgColor={settings.BackgroundColor}
                    step={settings.Step}
                    readOnly={!settings.EnableOutput}
                    lineCap="round"
                    angleArc={250}
                    angleOffset={-125}
                />
            </div>
        );
    }
    // ---------------------------------------------------------------------------------------------------
    handleChange = (newValue) => {
        this.setState({ value: newValue });
        // TODO: define if we need to have a streaming of data even if this have all the intermitidate values
    };
    // ---------------------------------------------------------------------------------------------------
    handleChangeEnd = (newValue) => {
        this.setState({ value: newValue });

        // check if new value have output message
        if (this.props.WidgetData.KnobSettings.EnableOutput)
            this.checkForOutputMessages(newValue);
    };
    // ---------------------------------------------------------------------------------------------------
    checkForOutputMessages(newValue: number) {
        if (this.props.WidgetData.KnobSettings.StreamingOutput)
            this.props.SendPortEvent(PortKeys.Output, newValue);
        else
            this.props.WidgetData.KnobSettings.OutputMessages.forEach((element) => {
                if (newValue == element.Value) {
                    // send port event with clicked feature data
                    this.props.SendPortEvent(PortKeys.Output, element.Message);
                }
            });
    };
    // ---------------------------------------------------------------------------------------------------
    updatePortValue(portKey: string, portState: string) {
        try {
            if (portKey == PortKeys.Input && !IsNullOrEmpty(portState)) {
                const settings = this.props.WidgetData.KnobSettings;
                let data: number = JSON.parse(portState);
                this.setState({ value: data });

                // check if port value is within the limits and send back the value
                if (settings.EnableOutput)
                    if (data >= settings.MinValue && data <= settings.MaxValue)
                        this.checkForOutputMessages(data);
            }
        }
        catch (ex) {
            DebugEx.TraceError(ex, portKey, portState);
        }
    }
    // ---------------------------------------------------------------------------------------------------
}

// =======================================================================================================
interface KnobWidgetSettingsState {
    widgetData: KnobData;
}

interface IOutputMessagesEditor {
    widgetData: KnobData;
    validateValues: (data: IOutputMessages[], minValue: number, maxValue: number) => boolean;
}

class KnobWidgetSettings extends React.Component<IWidgetSettingsProps<KnobData>, KnobWidgetSettingsState> {
    // ---------------------------------------------------------------------------------------------------
    constructor(props: IWidgetSettingsProps<KnobData>) {
        super(props);
        this.state = { widgetData: _.cloneDeep(this.props.WidgetData) };
        this.saveData = this.saveData.bind(this);
    }
    // ---------------------------------------------------------------------------------------------------
    saveData() {
        if (isDefined(this.props.onWidgetDataUpdate))
            this.props.onWidgetDataUpdate(this.state.widgetData);
    }
    // ------------------------------------------------------------------------------------------------
    render() {
        return <SettingsList
            AutoSave={true}
            Data={this.state.widgetData}
            SettingsMap={[
                {
                    name: '',
                    elements: [{
                        type: SettingsType.Number,
                        label: 'Min Value',
                        dataPath: 'KnobSettings.MinValue',
                        tooltip: 'Minimum value of gauge.',
                        oneLine: true
                    },
                    {
                        type: SettingsType.Number,
                        label: 'Max Value',
                        dataPath: 'KnobSettings.MaxValue',
                        tooltip: 'Maximum value of gauge.',
                        oneLine: true
                    },
                    {
                        type: SettingsType.Number,
                        label: 'Step',
                        dataPath: 'KnobSettings.Step',
                        tooltip: 'Set Step.',
                        oneLine: true
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Input Port',
                        dataPath: 'KnobSettings.EnableInput',
                        tooltip: 'Whether the input port should be enabled or disabled.'
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Output Port',
                        dataPath: 'KnobSettings.EnableOutput',
                        tooltip: 'Whether the output port should be enabled or disabled. If disabled the user input is not enabled.'
                    },
                    {
                        type: SettingsType.Dialog,
                        label: 'Output Messages',
                        dataPath: '',
                        tooltip: 'Output message to be sent when a certain value is clicked/tapped. (Used only when output port is enabled).',
                        icon: <i className="fas fa-pen" style={{ fontSize: 16 }} />,
                        isHidden: (data: KnobData) =>
                            !data.KnobSettings.EnableOutput ||
                            data.KnobSettings.StreamingOutput,
                        dialogConf: {
                            title: 'Output Messages',
                            contentStaticSize: 0.5,
                            initPrivData: (data) => {
                                return {
                                    widgetData: _.cloneDeep(data),
                                    validateValues: (data, minValue, maxValue) => {
                                        // Parameters validation.
                                        if (!isDefined(data) ||
                                            !isNumeric(minValue) ||
                                            !isNumeric(maxValue))
                                            return false;

                                        let canProceed: boolean = true;
                                        _.forEach(data, v => {
                                            // Is out of bounds?
                                            if (v.Value >= minValue &&
                                                v.Value <= maxValue) {
                                                v.OutOfBounds = false;
                                            }
                                            else {
                                                v.OutOfBounds = true;
                                                canProceed = false;
                                            }
                                        });

                                        // Return validation response.
                                        return canProceed;
                                    }
                                };
                            },
                            actionsFunc: (di) => [
                                <IconButton
                                    key='add-new-button'
                                    touch
                                    tooltip='Add new message'
                                    iconStyle={{ color: 'white' }}
                                    onClick={() => {
                                        // Add new message and update dialog's data.
                                        di.privData
                                            .widgetData
                                            .KnobSettings
                                            .OutputMessages
                                            .push({
                                                Value: di
                                                    .privData
                                                    .widgetData
                                                    .KnobSettings
                                                    .MinValue,
                                                Message: '',
                                                OutOfBounds: false
                                            });
                                        di.updatePrivData(di.privData);
                                    }}>
                                    <AddIcon />
                                </IconButton>,
                                <FlatButton
                                    secondary
                                    label='Cancel'
                                    onClick={di.closeDialogCB} />,
                                <FlatButton
                                    primary
                                    label='Save'
                                    onClick={() => {
                                        const opts = di.privData.widgetData.KnobSettings;
                                        if (di.privData.validateValues(opts.OutputMessages, opts.MinValue, opts.MaxValue)) {
                                            // Update dialog's data and close it.
                                            di.updateData(_.cloneDeep(di.privData.widgetData));
                                            di.closeDialogCB();
                                        }
                                    }} />
                            ],
                            bodyFunc: (w, h, di) => {
                                return <div>
                                    <Table
                                        selectable={false}
                                        multiSelectable={false}>
                                        <TableHeader
                                            displaySelectAll={false}
                                            adjustForCheckbox={false}
                                            enableSelectAll={false}>
                                            <TableRow>
                                                <TableHeaderColumn
                                                    colSpan={2}
                                                    style={{ textAlign: 'center' }}>
                                                    {'Value'}
                                                </TableHeaderColumn>
                                                <TableHeaderColumn
                                                    colSpan={2}
                                                    style={{ textAlign: 'center' }}>
                                                    {'Message'}
                                                </TableHeaderColumn>
                                                <TableHeaderColumn colSpan={1}>
                                                </TableHeaderColumn>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody
                                            stripedRows
                                            displayRowCheckbox={false}>
                                            {_.map(di
                                                .privData
                                                .widgetData
                                                .KnobSettings
                                                .OutputMessages, (v, i) => {
                                                    return (
                                                        <TableRow key={'output-message-row-' + i}>
                                                            <TableRowColumn
                                                                colSpan={2}
                                                                style={{ textAlign: 'center' }}>
                                                                <TextField
                                                                    fullWidth
                                                                    key={'value-' + i}
                                                                    id={'value-' + i}
                                                                    type='number'
                                                                    value={v.Value}
                                                                    errorText={(v.OutOfBounds) ? 'Out of bounds' : ''}
                                                                    onChange={(e: React.FormEvent<{}>, newValue: string) => {
                                                                        // Update value.
                                                                        di.privData
                                                                            .widgetData
                                                                            .KnobSettings
                                                                            .OutputMessages[i]
                                                                            .Value = parseFloat(newValue);

                                                                        // Validate values.
                                                                        di.privData.validateValues(
                                                                            di.privData
                                                                                .widgetData
                                                                                .KnobSettings
                                                                                .OutputMessages,
                                                                            di.privData.widgetData.KnobSettings.MinValue,
                                                                            di.privData.widgetData.KnobSettings.MaxValue
                                                                        );

                                                                        // Update dialog's data.
                                                                        di.updatePrivData(di.privData);
                                                                    }} />
                                                            </TableRowColumn>
                                                            <TableRowColumn
                                                                colSpan={2}
                                                                style={{ textAlign: 'center' }}>
                                                                <TextField
                                                                    fullWidth
                                                                    key={'message-' + i}
                                                                    id={'message-' + i}
                                                                    value={v.Message}
                                                                    onChange={(e: React.FormEvent<{}>, newValue: string) => {
                                                                        // Update message.
                                                                        di.privData
                                                                            .widgetData
                                                                            .KnobSettings
                                                                            .OutputMessages[i]
                                                                            .Message = newValue;
                                                                        di.updatePrivData(di.privData);
                                                                    }} />
                                                            </TableRowColumn>
                                                            <TableRowColumn
                                                                colSpan={1}
                                                                style={{ textAlign: 'center' }}>
                                                                <IconButton
                                                                    key={'delete-button-' + i}
                                                                    onClick={() => {
                                                                        // Delete button.
                                                                        di.privData
                                                                            .widgetData
                                                                            .KnobSettings
                                                                            .OutputMessages
                                                                            .splice(i, 1);
                                                                        di.updatePrivData(di.privData);
                                                                    }}>
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            </TableRowColumn>
                                                        </TableRow>
                                                    );
                                                })}
                                        </TableBody>
                                    </Table>
                                </div>;
                            }
                        } as SettingsDialogConf<KnobData, IOutputMessagesEditor>
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Output Streaming',
                        dataPath: 'KnobSettings.StreamingOutput',
                        tooltip: 'Output message to be sent without and transformation. (Used only when output port is enabled).'
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Background Color',
                        dataPath: 'KnobSettings.BackgroundColor',
                        tooltip: 'Background color of the widget. Check available color formats.'
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Foreground Color',
                        dataPath: 'KnobSettings.ForegroundColor',
                        tooltip: 'Color used to display the area of the measurement. Check available color formats.'
                    },
                    {
                        type: SettingsType.Highlights,
                        label: 'Highlights',
                        dataPath: 'KnobSettings.highlights',
                        tooltip: 'Define different colors for value ranges.',
                        icon: <i className="fas fa-pen" style={{ fontSize: 16 }} />,
                        minValue: 'KnobSettings.MinValue',
                        maxValue: 'KnobSettings.MaxValue'
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Counter-Clockwise',
                        dataPath: 'KnobSettings.reverseMode',
                        tooltip: 'Change the direction of progression.'
                    }]
                }]}
            onCheckData={(data) => {
                const settings = data.KnobSettings;
                // todo: use Micro.ts function (if we don't have this kind of helper function, export to Micro.ts)
                const isNumber = (v) => {
                    return !isNaN(parseFloat(v)) && isFinite(v);
                };

                let isValid: boolean = true;
                let errorMsg: string = '';

                if (!isNumber(settings.MinValue)) {
                    errorMsg = 'Min Value should be numeric';
                    isValid = false;
                }
                else if (!isNumber(settings.MaxValue)) {
                    errorMsg = 'Max Value should be numeric';
                    isValid = false;
                }
                else if (settings.MinValue > settings.MaxValue) {
                    errorMsg = 'Max Value should be greater than Min Value';
                    isValid = false;
                }
                else {
                    const highlightsRes = validateHighlightsWithMsg(settings.MinValue, settings.MaxValue, settings.highlights);
                    if (!highlightsRes.valid)
                        return highlightsRes;
                }

                return {
                    valid: isValid,
                    errorMsg: errorMsg
                };
            }}
            onSaveData={(data) => {
                this.setState(
                    { widgetData: data },
                    () => this.saveData());
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
    // ---------------------------------------------------------------------------------------------------
}
// =======================================================================================================
// Gauge Description interface
const enum PortKeys {
    Input = "input",
    Output = "output"
}
export class KnobWidget extends IWidgetThingsClass<KnobData> {
    // ------------------------------------------------------------------------------------------------
    public Render(key: string, width: number, height: number): React.ReactNode {
        return <Knob />;
    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <KnobWidgetSettings
            key={"Settings-" + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------
    updateThing() {
        let wData = this.WidgetData;
        let webNode = WebNode.GetInstanceKey(this.NodeKey);
        let thing = webNode.GetThing(this.ThingKey);

        // Clean old ports
        thing.Ports = {};

        // Add the ports
        this.GetPortsFromSettings(wData.KnobSettings).forEach((p) => {
            thing.AddPort(new WebThingPort(p, WebThingPortType.Data), false);
        });

        webNode.GetModule(this.WebModuleKey).UpdateThing(thing);
    }
    // ------------------------------------------------------------------------------------------------
    private GetPortsFromSettings(KnobSettings: KnobSettings): Plegma.Port[] {
        const ports: Plegma.Port[] = [];
        if (KnobSettings.EnableInput) {
            ports.push({
                Name: "Input",
                PortKey: PortKeys.Input,
                Type: Plegma.ePortType.Integer,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: "",
                State: ""
            })
        }
        if (KnobSettings.EnableOutput) {
            ports.push({
                Name: "Output",
                PortKey: PortKeys.Output,
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.None,
                Description: "",
                State: ""
            });
        }
        return ports;
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        let wData = this.WidgetData;

        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Ports
        let ports: WebThingPort[] = this.GetPortsFromSettings(wData.KnobSettings)
            .map((port) => new WebThingPort(port, WebThingPortType.Data));

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(group?: string): KnobData {
        return _.cloneDeep({
            KnobSettings: {
                MinValue: 0,
                MaxValue: 100,
                ForegroundColor: "rgba(102, 204, 102, 1)",
                BackgroundColor: "rgba(238, 238, 238, 1)",
                DisplayInput: true,
                EnableInput: true,
                EnableOutput: true,
                Step: 5,
                OutputMessages: [],
                StreamingOutput: true,
                highlights: [],
                reverseMode: false
            }
        });
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return KnobWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(group: string): IWidgetDescription {
        let data: KnobData = KnobWidget.GetDefaultWidgetDataStatic(group);
        return {
            Title: "Knob",
            WidgetTitle: "",
            Key: "KnobWidget",
            Type: "KnobWidget",
            Data: data,
            Description: "Knob widget",
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + "/png/knob.png"
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary(): LocalizationMap {
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            return {
                [WidgetsThingsStructs.WidgetLocalizationKeys.Title]: this.Description.WidgetTitle
            };
        else
            // nothing to expose
            return {};
    };
    // ------------------------------------------------------------------------------------------------
    GetDataPortKeyStatic(): string { return PortKeys.Input; };
    // ------------------------------------------------------------------------------------------------
}
// =======================================================================================================